(ns admin.events.containers.curriculum-reports
  "Responsible for fetching data for the curriculum reports page"
  (:require [kyber.router :refer [ensure-resource-v2]]
            [kyber.events.eql :as eql]
            [kyber.events.utils :refer [kyber-event-fx add-auth]]
            [re-frame.core :as re-frame]
            [kyber.lib.error-reporting :as ke]
            [admin.router.utils :as aru]
            [kyber.lib.kyber-db :as kyber-db]))

(defn- ->analytics-event [event]
  (keyword "curriculum-reports" (name event)))

(re-frame/reg-event-db
 ::clear-csv-report-message
 (fn [db _]
   (dissoc db :csv-report-message)))

(defn fetch-learners-success
  [{:keys [db]} [event-name response]]
  (let [curriculum (:>/curriculum response)]
    {:db (kyber-db/tx-record db {:model :curriculum} curriculum)
     :dispatch-n [[:analytics/save-datapoint
                   {:event (->analytics-event event-name)
                    :event-version 0}]]}))

(kyber-event-fx ::fetch-learners-success fetch-learners-success)

(defn fetch-learners-failure
  [_ [event-name _err]]
  {:dispatch-n [[:analytics/save-datapoint
                 {:event (->analytics-event event-name)
                  :event-version 0}]]})

(kyber-event-fx ::fetch-learners-failure fetch-learners-failure)

(defn fetch-learners-request
  [{:keys [db]} [event-name {:keys [params] :as context}]]
  (let [{:keys [current-page page-size sort-order sort-key tag-groups include-unpublished?]
         :or {page-size 10
              current-page 0
              sort-order :desc
              sort-key :average_readiness
              include-unpublished? false}}
        (aru/xform-query
          {:query (:query context)
           :xforms {:page-size js/parseInt
                    :current-page js/parseInt
                    :sort-order keyword
                    :sort-key keyword
                    :tag-groups aru/parse
                    :include-unpublished? aru/parse-boolean}})
        
        {:keys [curriculum-uuid]} params
        params `[{(:>/curriculum {:curriculum/uuid ~curriculum-uuid})
                  [:curriculum/uuid
                   :curriculum/name
                   :curriculum/description
                   {(:curriculum.starters/cached-stats {:tag-groups ~tag-groups})
                    [:activity/uuid
                     :activity/name
                     :activity.stats/starters-count
                     :activity.stats/completers-count
                     :activity.stats/readiness-distribution]}
                   {:curriculum/valid-tags
                    [:content-provider.tag-group/uuid
                     :content-provider.tag-group/name
                     :content-provider.tag-group/description
                     {:content-provider.tag-group/values
                      [:content-provider.tag/uuid
                       :content-provider.tag/name]}]}
                   {(:curriculum/activities
                     {:include-unpublished? ~include-unpublished?})
                    [:activity/uuid
                     :activity/name]}
                   {(:curriculum/starters {:limit ~page-size
                                           :offset ~(* current-page page-size)
                                           :sort-order ~sort-order
                                           :sort-key ~sort-key
                                           :tag-groups ~tag-groups})
                    [:curriculum.starters/page-info
                     {:curriculum.starters/edges
                      [{:curriculum/registration
                        [:content-provider.tag-group/name
                         :content-provider.tag/name]}
                       :user/uuid
                       :user/family-name
                       :user/given-name
                       :user/email-address
                       :curriculum-registration/created-at
                       :curriculum.learner/started-at
                       :curriculum.learner/completed-at
                       {:curriculum.session/activities
                        [:user/uuid
                         :activity/uuid
                         :activity/name
                         :activity/published
                         :activity.user/has-tags?
                         :activity/is-supplemental?
                         :activity/external-activity-link
                         :activity-session/readiness
                         :activity-session.user/readiness-offset
                         :activity-session/started-at
                         :activity-session/completed-at
                         :activity-session/mindset
                         {:activity-session/pre-test
                          [:questionnaire.attempts/has-tested-out?]}]}]}
                     :curriculum.starters/tags-with-registrations]}]}]]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event (->analytics-event event-name)
                    :event-version 0}]]
     :request
     (eql/request db
                  {:name event-name
                   :params params
                   :on-success [::fetch-learners-success]
                   :on-failure [::fetch-learners-failure]})}))

(kyber-event-fx ::fetch-learners fetch-learners-request)

(defn async-fetch-learners-request-data-polling-complete-success
  [{:keys [db]} [event-name]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::async-fetch-learners-request-data-polling-complete-success async-fetch-learners-request-data-polling-complete-success)

(defn async-fetch-learners-request-data-polling-complete
  [{:keys [db]} [event-name]]
  {:db db
   :request-mock {:name event-name
                  :mock {:time 100}
                  :on-success [::async-fetch-learners-request-data-polling-complete-success]}})

(kyber-event-fx ::async-fetch-learners-request-data-polling-complete async-fetch-learners-request-data-polling-complete)

(defn async-fetch-learners-request-data-failure
  [{:keys [db]} [event-name params err]]
  {:db (-> db (update :polling-in-progress disj ::async-fetch-learners-request))
   :dispatch-n [[:report-error (ke/->failed-request-error event-name (assoc params :error err))]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::async-fetch-learners-request-data-failure async-fetch-learners-request-data-failure)

(defn async-fetch-learners-request-data-success
  [{:keys [db]} [event-name id raw-response]]
  (let [async-request
        (:>/async-request raw-response)

        {async-response :async.request/response
         async-status :async.request/status}
        async-request

        {:>/keys [curriculum]}
        (if (some? async-request)
          async-response
          raw-response)]
    (cond
      (or (and (some? async-request)
               (= :complete async-status)
               (some? async-response))
          (and (nil? async-request)
               raw-response))
      {:db  (let [activity-uuids
                  (->> curriculum
                       :curriculum/activities
                       (mapv :activity/uuid)
                       set)

                  curriculum-with-filtered-sessions
                  (-> curriculum
                      (update-in [:curriculum/starters :curriculum.starters/edges]
                                 (fn [starters]
                                   (mapv (fn [starter]
                                           (update starter
                                                   :curriculum.session/activities
                                                   (fn [activity-sessions]
                                                     (filterv #(contains? activity-uuids (:activity/uuid %))
                                                              activity-sessions)))) starters)))
                      (update :curriculum.starters/cached-stats
                              (fn [stats]
                                (filterv #(contains? activity-uuids (:activity/uuid %)) stats))))]
              (-> db
                 (update :polling-in-progress disj ::async-fetch-learners-request)
                 (kyber-db/tx-record {:model :curriculum} curriculum-with-filtered-sessions)))
       :dispatch-n [[:analytics/save-datapoint
                       {:event (->analytics-event event-name)
                        :event-version 0}]
                    [::async-fetch-learners-request-data-polling-complete]]}

      (and (some? async-request)
           (= :executing async-status))
      {:dispatch-later [{:ms 2000 :dispatch [::async-fetch-learners-request-data {:async.request/id id}]}]}

      :else
      {:dispatch [::async-fetch-learners-request-data-failure]})))

(kyber-event-fx ::async-fetch-learners-request-data-success async-fetch-learners-request-data-success)

(defn async-fetch-learners-request-data
  [{:keys [db]} [event-name {:async.request/keys [id]}]]
  {:request (eql/request
             db
             {:name event-name
              :params `[{(:>/async-request {:async.request/id ~id})
                         [:async.request/status
                          :async.request/response]}]
              :on-success [::async-fetch-learners-request-data-success id]
              :on-failure [::async-fetch-learners-request-data-failure]})})

(kyber-event-fx ::async-fetch-learners-request-data async-fetch-learners-request-data)

(defn async-fetch-learners-request-id-failure
  [{:keys [db]} [event-name params err]]
  {:db (-> db (update :polling-in-progress disj ::async-fetch-learners-request))
   :dispatch-n [[:report-error (ke/->failed-request-error event-name (assoc params :error err))]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::async-fetch-learners-id-request-failure async-fetch-learners-request-id-failure)

(defn async-fetch-learners-request-id
  [{:keys [db]} [event-name {:keys [params] :as context}]]
  (let [{:keys [current-page page-size sort-order sort-key tag-groups include-unpublished?]
         :or {page-size 10
              current-page 0
              sort-order :desc
              sort-key :average_readiness
              include-unpublished? false}}
        (aru/xform-query
          {:query (:query context)
           :xforms {:page-size js/parseInt
                    :current-page js/parseInt
                    :sort-order keyword
                    :sort-key keyword
                    :tag-groups aru/parse
                    :include-unpublished? aru/parse-boolean}})
        
        {:keys [curriculum-uuid]} params
        params `[{(:>/curriculum {:curriculum/uuid ~curriculum-uuid})
                  [:curriculum/uuid
                   :curriculum/name
                   :curriculum/description
                   {(:curriculum.starters/cached-stats {:tag-groups ~tag-groups})
                    [:activity/uuid
                     :activity/name
                     :activity.stats/starters-count
                     :activity.stats/completers-count
                     :activity.stats/readiness-distribution]}
                   {:curriculum/valid-tags
                    [:content-provider.tag-group/uuid
                     :content-provider.tag-group/name
                     :content-provider.tag-group/description
                     {:content-provider.tag-group/values
                      [:content-provider.tag/uuid
                       :content-provider.tag/name]}]}
                   {(:curriculum/activities
                     {:include-unpublished? ~include-unpublished?})
                    [:activity/uuid
                     :activity/name]}
                   {(:curriculum/starters {:limit ~page-size
                                           :offset ~(* current-page page-size)
                                           :sort-order ~sort-order
                                           :sort-key ~sort-key
                                           :tag-groups ~tag-groups})
                    [:curriculum.starters/page-info
                     {:curriculum.starters/edges
                      [{:curriculum/registration
                        [:content-provider.tag-group/name
                         :content-provider.tag/name]}
                       :user/uuid
                       :user/family-name
                       :user/given-name
                       :user/email-address
                       :curriculum-registration/created-at
                       :curriculum.learner/started-at
                       :curriculum.learner/completed-at
                       {:curriculum.session/activities
                        [:user/uuid
                         :activity/uuid
                         :activity/name
                         :activity/published
                         :activity.user/has-tags?
                         :activity/is-supplemental?
                         :activity/external-activity-link
                         :activity-session/readiness
                         :activity-session.user/readiness-offset
                         :activity-session/started-at
                         :activity-session/completed-at
                         :activity-session/mindset
                         {:activity-session/pre-test
                          [:questionnaire.attempts/has-tested-out?]}]}]}
                     :curriculum.starters/tags-with-registrations]}]}]]
    {:db (-> db (update :polling-in-progress conj ::async-fetch-learners-request))
     :dispatch-n [[:analytics/save-datapoint
                   {:event (->analytics-event event-name)
                    :event-version 0}]]
     :request
     (eql/request db
                  {:name event-name
                   :headers (assoc (add-auth db)
                                   "X-Kyber-Async" true)
                   :params params
                   :on-success [::async-fetch-learners-request-data]
                   :on-failure [::async-fetch-learners-request-id-failure]})}))

(kyber-event-fx ::async-fetch-learners-request-id async-fetch-learners-request-id)

(def fetch-learners
  (partial
   ensure-resource-v2
   ::async-fetch-learners-request-data-polling-complete
   #(re-frame/dispatch [::async-fetch-learners-request-id {:params %1 :query %2}])))

;; CSV Report Generation Events (Async Lambda Pattern)

(def csv-report-failure-message "Failed to generate CSV report. Please try again.")

(defn async-generate-csv-report-data-failure
  [{:keys [db]} [event-name params err]]
  {:db (-> db
           (update :polling-in-progress disj ::async-generate-csv-report)
           (assoc :csv-report-message csv-report-failure-message))
   :dispatch-n [[:notifier/create-toast
                 {:message csv-report-failure-message
                  :status :error
                  :duration 9000}]
                [:report-error (ke/->failed-request-error event-name (assoc params :error err))]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::async-generate-csv-report-data-failure async-generate-csv-report-data-failure)

(defn async-generate-csv-report-data-success
  [{:keys [db]} [event-name id raw-response]]
  (let [async-request (:>/async-request raw-response)
        {async-response :async.request/response
         async-status :async.request/status} async-request
        response (if (some? async-request)
                   async-response
                   raw-response)
        message (get-in response ['curriculum/generate-csv-report :curriculum/csv-report-message])]
    (cond
      (or (and (some? async-request)
               (= :complete async-status)
               (some? async-response))
          (and (nil? async-request)
               raw-response))
      {:db (-> db
               (update :polling-in-progress disj ::async-generate-csv-report)
               (assoc :csv-report-message message))
       :dispatch-n [[:notifier/create-toast
                     {:message message
                      :duration 9000}]
                    [:analytics/save-datapoint
                     {:event (->analytics-event event-name)
                      :event-version 0}]]}

      (and (some? async-request)
           (= :executing async-status))
      {:dispatch-later [{:ms 2000 :dispatch [::async-generate-csv-report-data {:async.request/id id}]}]}

      :else
      {:dispatch [::async-generate-csv-report-data-failure]})))

(kyber-event-fx ::async-generate-csv-report-data-success async-generate-csv-report-data-success)

(defn async-generate-csv-report-data
  [{:keys [db]} [event-name {:async.request/keys [id]}]]
  {:request (eql/request
             db
             {:name event-name
              :params `[{(:>/async-request {:async.request/id ~id})
                         [:async.request/status
                          :async.request/response]}]
              :on-success [::async-generate-csv-report-data-success id]
              :on-failure [::async-generate-csv-report-data-failure]})})

(kyber-event-fx ::async-generate-csv-report-data async-generate-csv-report-data)

(defn async-generate-csv-report-id-failure
  [{:keys [db]} [event-name params err]]
  {:db (-> db
           (update :polling-in-progress disj ::async-generate-csv-report)
           (assoc :csv-report-message csv-report-failure-message))
   :dispatch-n [[:notifier/create-toast
                 {:message csv-report-failure-message
                  :status :error
                  :duration 9000}]
                [:report-error (ke/->failed-request-error event-name (assoc params :error err))]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::async-generate-csv-report-id-failure async-generate-csv-report-id-failure)

(defn generate-csv-report
  [{:keys [db]} [event-name {curriculum-uuid :curriculum/uuid}]]
  {:db (-> db
           (update :polling-in-progress conj ::async-generate-csv-report)
           (dissoc :csv-report-message))
   :dispatch-n [[:analytics/save-datapoint
                 {:event (->analytics-event event-name)
                  :event-version 0}]]
   :request
   (eql/request db
                {:name event-name
                 :headers (assoc (add-auth db)
                                 "X-Kyber-Async" true)
                 :params `[(curriculum/generate-csv-report {:curriculum/uuid ~curriculum-uuid})]
                 :on-success [::async-generate-csv-report-data]
                 :on-failure [::async-generate-csv-report-id-failure]})})

(kyber-event-fx ::generate-csv-report generate-csv-report)
